import {
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import type { Icon } from '@phosphor-icons/react';
import { MagnifyingGlassIcon, TrashIcon, XIcon } from '@phosphor-icons/react';
import clsx from 'clsx';
import { FC, useMemo, useRef, useState } from 'react';
import { createAdminPageStyles } from '../../style';
import { ResourceItem, ResourceStatus } from '../types';

interface ResourcePickerProps {
  title: string;
  icon?: Icon;
  catalog: ResourceItem[];
  selected: ResourceItem[];
  showStatus?: boolean;
  onToggle: (item: ResourceItem, checked: boolean) => void;
  onRemove: (id: number) => void;
}

const STATUS_LABEL: Record<ResourceStatus, string> = {
  done: 'Fait',
  todo: 'Pas fait',
  progress: 'En cours',
};

const useStyles = createAdminPageStyles();

const ResourcePicker: FC<ResourcePickerProps> = ({
  title,
  icon: HeaderIcon,
  catalog,
  selected,
  showStatus = false,
  onToggle,
  onRemove,
}) => {
  const classes = useStyles();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selectedIds = useMemo(() => new Set(selected.map(item => item.id)), [selected]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return catalog;
    }
    return catalog.filter(
      item =>
        item.label.toLowerCase().includes(term) ||
        (item.group || '').toLowerCase().includes(term) ||
        (item.groupSubtitle || '').toLowerCase().includes(term)
    );
  }, [catalog, query]);

  const grouped = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; name: string; subtitle?: string; items: ResourceItem[] }
    >();
    filtered.forEach(item => {
      const key = item.groupId != null ? `id:${item.groupId}` : `name:${item.group || ''}`;
      const current = groups.get(key);
      if (current) {
        current.items.push(item);
        return;
      }
      groups.set(key, {
        key,
        name: item.group || '',
        subtitle: item.groupSubtitle,
        items: [item],
      });
    });
    return Array.from(groups.values());
  }, [filtered]);

  return (
    <div className={classes.resourceSection}>
      <div className={classes.resourceHeader}>
        {HeaderIcon ? <HeaderIcon size={20} weight="regular" aria-hidden="true" /> : null}
        {title}
      </div>
      <div
        className={classes.searchWrap}
        ref={wrapRef}
        onBlur={event => {
          if (!wrapRef.current?.contains(event.relatedTarget as Node)) {
            setOpen(false);
          }
        }}>
        <div className={classes.searchFieldWrap}>
          <TextField
            className={classes.textField}
            variant="outlined"
            placeholder="Rechercher"
            value={query}
            onFocus={() => setOpen(true)}
            onChange={event => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlassIcon size={20} color="#8B90A5" />
                </InputAdornment>
              ),
              endAdornment: query.trim() ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => setQuery('')}
                    aria-label="Effacer">
                    <XIcon size={16} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          {open && (
            <div className={classes.dropdown} onMouseDown={event => event.preventDefault()}>
              {filtered.length === 0 ? (
                <div className={classes.emptyHint}>Aucun résultat</div>
              ) : (
                grouped.map(group => (
                  <div key={group.key || 'all'}>
                    {group.name ? (
                      <div className={classes.dropdownGroup}>
                        {group.name}
                        {group.subtitle ? (
                          <span className={classes.dropdownGroupSubtitle}>{group.subtitle}</span>
                        ) : null}
                      </div>
                    ) : null}
                    {group.items.map(item => (
                      <div key={item.id} className={classes.dropdownItem}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selectedIds.has(item.id)}
                              onChange={event => onToggle(item, event.target.checked)}
                            />
                          }
                          label={item.label}
                        />
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {selected.length > 0 && (
        <div className={classes.selectedList}>
          {selected.map(item => (
            <div key={item.id} className={classes.selectedRow}>
              <span className={classes.selectedLabel}>{item.label}</span>
              {showStatus && item.status ? (
                <span
                  className={clsx(
                    classes.statusTag,
                    item.status === 'done' && classes.statusDone,
                    item.status === 'todo' && classes.statusTodo,
                    item.status === 'progress' && classes.statusProgress
                  )}>
                  {STATUS_LABEL[item.status]}
                </span>
              ) : null}
              <IconButton
                className={classes.iconBtn}
                type="button"
                onMouseDown={event => event.preventDefault()}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  setOpen(false);
                  onRemove(item.id);
                }}
                aria-label="Retirer">
                <TrashIcon size={18} />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResourcePicker;
